import React, { useState, useEffect } from 'react';
import { AlertTriangle, RefreshCw, Clock, CheckCircle, XCircle, Activity } from 'lucide-react';
import { supabase } from '../../../lib/supabase';

interface StuckReceipt {
  id: string;
  user_id: string;
  telegram_username: string;
  telegram_id: number;
  status: string;
  retry_count: number;
  minutes_stuck: number;
  created_at: string;
  last_retry_at: string | null;
}

interface ProcessingHealth {
  totalProcessed: number;
  successRate: number;
  avgProcessingTime: number;
  stuckCount: number;
  failedCount: number;
}

export default function StuckReceiptsMonitor() {
  const [stuckReceipts, setStuckReceipts] = useState<StuckReceipt[]>([]);
  const [health, setHealth] = useState<ProcessingHealth>({
    totalProcessed: 0,
    successRate: 100,
    avgProcessingTime: 0,
    stuckCount: 0,
    failedCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    loadData();

    if (autoRefresh) {
      const interval = setInterval(loadData, 10000); // Refresh every 10 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const loadData = async () => {
    try {
      await Promise.all([loadStuckReceipts(), loadHealthMetrics()]);
    } catch (error) {
      console.error('Error loading monitoring data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStuckReceipts = async () => {
    const { data, error } = await supabase.rpc('get_stuck_receipts', {
      minutes_threshold: 5,
    });

    if (error) {
      console.error('Error loading stuck receipts:', error);
      return;
    }

    setStuckReceipts(data || []);
  };

  const loadHealthMetrics = async () => {
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();

    const [receiptsResult, logsResult] = await Promise.all([
      supabase
        .from('receipts')
        .select('status, created_at, processing_started_at')
        .gte('created_at', oneHourAgo),
      supabase
        .from('processing_logs')
        .select('processing_duration_ms, status')
        .gte('created_at', oneHourAgo)
        .eq('status', 'success'),
    ]);

    const receipts = receiptsResult.data || [];
    const logs = logsResult.data || [];

    const completed = receipts.filter(
      (r) => r.status === 'parsed' || r.status === 'completed'
    );
    const stuck = receipts.filter(
      (r) =>
        r.status === 'pending' &&
        new Date(r.created_at).getTime() < Date.now() - 5 * 60 * 1000
    );
    const failed = receipts.filter((r) => r.status === 'failed');

    const successRate =
      receipts.length > 0 ? (completed.length / receipts.length) * 100 : 100;

    const avgDuration =
      logs.length > 0
        ? logs.reduce((sum, log) => sum + (log.processing_duration_ms || 0), 0) / logs.length
        : 0;

    setHealth({
      totalProcessed: receipts.length,
      successRate,
      avgProcessingTime: avgDuration / 1000,
      stuckCount: stuck.length,
      failedCount: failed.length,
    });
  };

  const handleRetry = async (receiptId: string) => {
    setRetrying(receiptId);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/retry-stuck-receipts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
        }
      );

      if (response.ok) {
        await loadData();
      }
    } catch (error) {
      console.error('Error retrying receipt:', error);
    } finally {
      setRetrying(null);
    }
  };

  const handleRetryAll = async () => {
    setRetrying('all');

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/retry-stuck-receipts`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
        }
      );

      if (response.ok) {
        await loadData();
      }
    } catch (error) {
      console.error('Error retrying receipts:', error);
    } finally {
      setRetrying(null);
    }
  };

  const getStatusColor = (count: number) => {
    if (count === 0) return 'text-green-600 bg-green-100';
    if (count <= 2) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Receipt Processing Monitor
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            Real-time monitoring of receipt processing health
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh
          </label>
          <button
            onClick={loadData}
            disabled={loading}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">Stuck Receipts</p>
              <p className={`text-3xl font-bold mt-1 ${getStatusColor(health.stuckCount)}`}>
                {health.stuckCount}
              </p>
            </div>
            <AlertTriangle
              className={`w-8 h-8 ${
                health.stuckCount === 0 ? 'text-gray-300' : 'text-red-500'
              }`}
            />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">Success Rate</p>
              <p className="text-3xl font-bold text-green-600 mt-1">
                {health.successRate.toFixed(1)}%
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-500" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">Avg Time</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">
                {health.avgProcessingTime.toFixed(1)}s
              </p>
            </div>
            <Clock className="w-8 h-8 text-blue-500" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">Failed</p>
              <p className="text-3xl font-bold text-red-600 mt-1">{health.failedCount}</p>
            </div>
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
        </div>

        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">Last Hour</p>
              <p className="text-3xl font-bold text-purple-600 mt-1">
                {health.totalProcessed}
              </p>
            </div>
            <Activity className="w-8 h-8 text-purple-500" />
          </div>
        </div>
      </div>

      {stuckReceipts.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Stuck Receipts Details
            </h3>
            <button
              onClick={handleRetryAll}
              disabled={retrying === 'all'}
              className="btn-primary flex items-center gap-2 text-sm"
            >
              <RefreshCw className={`w-4 h-4 ${retrying === 'all' ? 'animate-spin' : ''}`} />
              Retry All
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                    User
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                    Stuck Duration
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                    Retry Count
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                    Last Retry
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-600 dark:text-gray-300">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                {stuckReceipts.map((receipt) => (
                  <tr key={receipt.id}>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      @{receipt.telegram_username || 'Unknown'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                        {receipt.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {Math.floor(receipt.minutes_stuck)} min
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                      {receipt.retry_count}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                      {receipt.last_retry_at
                        ? new Date(receipt.last_retry_at).toLocaleTimeString()
                        : 'Never'}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleRetry(receipt.id)}
                        disabled={retrying === receipt.id}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                      >
                        <RefreshCw
                          className={`w-3 h-3 ${
                            retrying === receipt.id ? 'animate-spin' : ''
                          }`}
                        />
                        Retry
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {stuckReceipts.length === 0 && !loading && (
        <div className="card text-center py-12">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            All Systems Operational
          </h3>
          <p className="text-gray-600 dark:text-gray-300">
            No stuck receipts detected. Processing is working smoothly.
          </p>
        </div>
      )}
    </div>
  );
}
